/**
 * 電子發票開立 schema — REQ-20260602001 Phase 2C,REQ-20260915003 改串 ezPay。
 *
 * 手動開立(不依賴出貨單):選類別 → 填買方/載具/捐贈 → 填品項 → 送 ezPay 開立。
 * 發票號碼、隨機碼、條碼由 ezPay 回傳。
 */
import { z } from 'zod';

/** 發票類別:B2B 三聯式(有統編)/ B2C 二聯式 */
export const InvoiceTypeEnum = z.enum(['B2B', 'B2C']);
export type InvoiceTypeEnum = z.infer<typeof InvoiceTypeEnum>;

export const InvoiceTaxTypeEnum = z.enum(['TAXABLE', 'ZERO_RATE', 'EXEMPT']);
export type InvoiceTaxTypeEnum = z.infer<typeof InvoiceTaxTypeEnum>;

export const InvoiceStatusEnum = z.enum(['ISSUED', 'VOIDED', 'ALLOWANCE']);
export type InvoiceStatusEnum = z.infer<typeof InvoiceStatusEnum>;

export const InvoiceUploadStatusEnum = z.enum(['PENDING', 'UPLOADED', 'CONFIRMED', 'FAILED']);
export type InvoiceUploadStatusEnum = z.infer<typeof InvoiceUploadStatusEnum>;

/** ezPay 載具類別代碼:0 = 手機條碼、1 = 自然人憑證 */
export const InvoiceCarrierTypeEnum = z.enum(['0', '1']);
export type InvoiceCarrierTypeEnum = z.infer<typeof InvoiceCarrierTypeEnum>;

export const InvoiceLineInputSchema = z.object({
  description: z.string().trim().min(1, '品名必填').max(256),
  quantity: z.coerce.number().positive('數量須大於 0'),
  unit: z.string().trim().min(1, '單位必填').max(30),
  /** B2B 為未稅單價;B2C 為含稅單價(ezPay 規定) */
  unitPrice: z.coerce.number().min(0, '單價不可為負'),
  taxType: InvoiceTaxTypeEnum.default('TAXABLE'),
});
export type InvoiceLineInput = z.infer<typeof InvoiceLineInputSchema>;

export const IssueInvoiceSchema = z.object({
  type: InvoiceTypeEnum,
  /** 關聯客戶(選填,僅作冗餘關聯;買方資訊以下列欄位 freeze) */
  customerId: z.coerce.number().int().positive().optional(),
  /** 買方統編:B2B 必填 8 碼;B2C 留空 */
  buyerTaxId: z
    .string()
    .trim()
    .regex(/^\d{8}$/u, '買方統編須為 8 位數字')
    .optional()
    .or(z.literal('')),
  /** 買方名稱:B2C 必填(ezPay 規定);B2B 留空時以統編代入 */
  buyerName: z.string().trim().max(60).optional(),
  /** 載具類別(B2C;與捐贈互斥) */
  carrierType: InvoiceCarrierTypeEnum.optional().or(z.literal('')),
  carrierId: z.string().trim().max(64).optional(),
  /** 捐贈愛心碼(B2C;與載具互斥),3~7 碼數字 */
  donationCode: z
    .string()
    .trim()
    .regex(/^\d{3,7}$/u, '愛心碼須為 3~7 碼數字')
    .optional()
    .or(z.literal('')),
  taxRate: z.coerce.number().min(0).max(1).default(0.05),
  lines: z.array(InvoiceLineInputSchema).min(1, '至少一筆品項').max(999),
});
export type IssueInvoiceInput = z.infer<typeof IssueInvoiceSchema>;

export const InvoiceLineViewSchema = z.object({
  lineNo: z.number().int(),
  description: z.string(),
  quantity: z.string(),
  unit: z.string().nullable(),
  unitPrice: z.string(),
  amount: z.string(),
  taxType: InvoiceTaxTypeEnum,
});
export type InvoiceLineView = z.infer<typeof InvoiceLineViewSchema>;

export const InvoiceViewSchema = z.object({
  id: z.number().int(),
  invoiceNo: z.string(),
  randomNumber: z.string(),
  type: InvoiceTypeEnum,
  taxType: InvoiceTaxTypeEnum,
  status: InvoiceStatusEnum,
  invoiceDate: z.string(),
  period: z.string(),
  buyerTaxId: z.string().nullable(),
  buyerName: z.string().nullable(),
  carrierType: z.string().nullable(),
  carrierId: z.string().nullable(),
  donationCode: z.string().nullable(),
  salesAmount: z.string(),
  taxRate: z.string(),
  taxAmount: z.string(),
  totalAmount: z.string(),
  uploadStatus: InvoiceUploadStatusEnum,
  createdAt: z.string().datetime(),
  lines: z.array(InvoiceLineViewSchema),
});
export type InvoiceView = z.infer<typeof InvoiceViewSchema>;

export const InvoiceListItemSchema = z.object({
  id: z.number().int(),
  invoiceNo: z.string(),
  type: InvoiceTypeEnum,
  status: InvoiceStatusEnum,
  uploadStatus: InvoiceUploadStatusEnum,
  invoiceDate: z.string(),
  buyerTaxId: z.string().nullable(),
  buyerName: z.string().nullable(),
  totalAmount: z.string(),
});
export type InvoiceListItem = z.infer<typeof InvoiceListItemSchema>;

export const InvoiceListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  q: z.string().trim().optional(),
});
export type InvoiceListQuery = z.infer<typeof InvoiceListQuerySchema>;
